/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { volumeService } from '../services/api';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

export interface Volume {
  id: string;
  name: string;
  status: string;
  quota_gb?: number;
  used_gb?: number;
  nfs_enabled?: boolean;
  nfs_path?: string;
  nfs_server?: string;
  created_at: string;
  project_id: string;
}

export const useVolumes = (projectId: string) => {
  const queryClient = useQueryClient();

  const { data: volumesData, isLoading, isPending, isError, refetch } = useQuery({
    queryKey: ['volumes', projectId],
    queryFn: () => volumeService.list(projectId).then((res) => res.data),
    enabled: !!projectId,
  });

  // Handle various response formats
  const allVolumes: Volume[] = Array.isArray(volumesData?.data || volumesData)
    ? (volumesData?.data || volumesData)
    : volumesData?.volumes || [];
  
  // Filter out deleted volumes if status exists
  const volumes = allVolumes.filter((v) => v.status !== 'deleted');

  const createMutation = useMutation({
    mutationFn: (data: {
      name: string;
      quota_gb?: number;
      nfs_enabled?: boolean;
      billing_period?: string;
    }) => volumeService.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['volumes', projectId] });
      toast.success('Volume created successfully');
    },
    onError: (error: AxiosError<{ error?: string; message?: string }>) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create volume';
      toast.error(errorMessage);
    },
  });

  const updateQuotaMutation = useMutation({
    mutationFn: ({ id, quota_gb }: { id: string; quota_gb: number }) =>
      volumeService.updateQuota(id, projectId, quota_gb),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['volumes', projectId] });
      toast.success('Volume quota updated successfully');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to update volume quota');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => volumeService.delete(id, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['volumes', projectId] });
      toast.success('Volume deleted successfully');
    },
    onError: (error: AxiosError<{ error?: string }>) => {
      toast.error(error.response?.data?.error || 'Failed to delete volume');
    },
  });

  return {
    volumes,
    totalCount: volumes.length,
    isLoading: isLoading || isPending,
    isError,
    refetch,
    createVolume: createMutation.mutate,
    isCreating: createMutation.isPending,
    updateQuota: updateQuotaMutation.mutate,
    isUpdatingQuota: updateQuotaMutation.isPending,
    deleteVolume: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};
